import { AD_AUTHORITY, AD_CLIENT_ID } from '../constants';
import { ApiEndpoint } from './ApiDefinition';
import { JSON, JSONArray, JSONDateTime, JSONObject } from './types';
import { FetchOptions } from '../util/fetchApi';

/** Opt-in header for cookie sessions; must match SessionCookieConfig.OPT_IN_HEADER on the backend. */
export const SESSION_HEADER = { 'X-UWE-Session': 'cookie' };

/**
 * Options for every endpoint that may set or replace the session cookie. `credentials: 'include'` is what
 * lets a separately hosted API's Set-Cookie land (dev/staging); the header opts into cookie sessions.
 * No 401 retry: these endpoints establish the session, so there is nothing to refresh.
 */
export const SESSION_OPTIONS: FetchOptions = {
  credentials: 'include',
  headers: SESSION_HEADER,
  onUnauthorized: undefined,
};

/**
 * For endpoints authenticated by the bearer token that also replace the cookie (impersonation). Unlike
 * SESSION_OPTIONS they keep the default 401 handling: an expired access token is refreshed and retried.
 */
const BEARER_SESSION_OPTIONS: FetchOptions = {
  credentials: 'include',
  headers: SESSION_HEADER,
};

export const AuthApi = {
  getADClientId: ApiEndpoint<void, string>({
    url: 'security/ad-client-id',
  }),

  getAuthConfig: ApiEndpoint<void, AuthConfigDTO>({
    url: 'security/auth-config'
  }),

  login: ApiEndpoint<
    {
      email: string;
      password: string;
    },
    AuthenticateResponse
  >({
    method: 'post',
    url: 'security/login',
    body: ({ email, password }) => ({
      email: email || '',
      password: password || '',
    }),
    options: SESSION_OPTIONS,
  }),

  verifyMfa: ApiEndpoint<
    {
      otp: string;
      token?: string;
    },
    AuthenticateResponse
  >({
    method: 'post',
    url: 'security/verify-mfa',
    body: ({ otp, token }) => ({
      options: {
        otp: otp || '',
      },
      token: token || '',
    }),
    options: SESSION_OPTIONS,
  }),

  impersonate: ApiEndpoint<
    {
      userId: number;
    },
    SessionAuthenticateResponse
  >({
    method: 'post',
    url: 'security/impersonate',
    body: ({ userId }) => ({ userId }),
    options: BEARER_SESSION_OPTIONS,
  }),

  stopImpersonation: ApiEndpoint<void, SessionAuthenticateResponse>({
    method: 'post',
    url: 'security/stop-impersonation',
    options: BEARER_SESSION_OPTIONS,
  }),

  loginWithToken: ApiEndpoint<
    {
      token: string;
    },
    AuthenticateResponse
  >({
    method: 'post',
    url: 'security/login-with-token',
    body: ({ token }) => ({ token }),
    options: SESSION_OPTIONS,
  }),

  loginKerberosSso: ApiEndpoint<void, AuthenticateResponse>({
    method: 'post',
    url: 'security/login-sso',
    options: SESSION_OPTIONS,
  }),

  verifyToken: ApiEndpoint<void, boolean>({
    method: 'post',
    url: 'security/check',
  }),

  refresh: ApiEndpoint<void, SessionAuthenticateResponse>({
    method: 'post',
    url: 'security/refresh',
  }),

  recoverPassword: ApiEndpoint<{ email: string }, string>({
    method: 'post',
    url: ({ email }) => `security/password-recovery/${email}`,
  }),

  resetPassword: ApiEndpoint<
    {
      token: string;
      password: string;
      passwordConfirm: string;
    },
    string
  >({
    method: 'post',
    url: ({ token }) => `security/password-reset/${token}`,
    body: ({ password, passwordConfirm }) => ({
      password,
      passwordConfirm,
    }),
  }),

  getSelf: ApiEndpoint<void, UserDetailsDTO>({
    url: 'security/self',
  }),

  getSignature: ApiEndpoint<void, any>({
    url: 'security/self/signature',
  }),

  updateSelf: ApiEndpoint<UpdateSelfUserDTO, SelfUpdateResponseDTO>({
    method: 'post',
    url: 'security/self',
    body: data => data,
  }),

  generateAPIKeyToken: ApiEndpoint<void, string>({
    method: 'post',
    url: 'security/self/api-key/new-token',
  }),

  revokeAPIKeyToken: ApiEndpoint<void, string>({
    method: 'post',
    url: 'security/self/api-key/revoke-token',
  }),
};

// Serialized by name (the backend enum carries JsonStringEnumConverter).
export enum MfaChallengeType {
  EMAIL_OTP = 'EMAIL_OTP',
}

export type UpdateSelfUserDTO = {
  firstName: string;
  lastName: string;
  phone: string;
  extension: string;
  password: string;
  passwordConfirm: string;
  signature: string;
  notifyDesktop: boolean;
  notifyEmail: boolean;
  timeZoneId: string;
  isOnLeave: boolean;
};

export type AuthConfigDTO = {
  basic: boolean;
  azureSSO?: {
    authority: string
    clientId: string
  };
  ldap?: {
    basic: boolean;
    sso: boolean;
  };
};

export type AuthenticateResponse = {
  user: UserAuthDTO;
  token: string;
  logOutUrl: string;
  mfaVerificationRequired?: boolean;
  mfaVerificationType?: MfaChallengeType;
};

/** Response of the endpoints that resume or switch a session: who, if anyone, is impersonating. */
export type SessionAuthenticateResponse = AuthenticateResponse & {
  impersonatorUserId?: number | null;
  impersonatorName?: string | null;
  /** When the session signs out for inactivity; null for a login without a session. */
  idleExpiresAt?: JSONDateTime | null;
  /** Seconds from the server's clock to idleExpiresAt. Count down from this: the client's clock may be off. */
  idleExpiresInSeconds?: number | null;
};

export type UserDetailsDTO = {
  id: number;
  email: string;
  createdAt?: JSONDateTime;
  isActive: boolean;
  isOnLeave: boolean;
  hasCompletedProfile: boolean;
  changePassword: boolean;
  firstName: string;
  lastName: string;
  organizationalUnitId: number;
  phone: string;
  extension: string;
  position: string;
  jediId: number;
  accessProfileId: number;
  signature: string;
  organizationalUnitRoles: UserOrganizationalUnitRoleDTO[];
  branchId: number;
  extras: JSON;
  notifyDesktop: boolean;
  notifyEmail: boolean;
  timeZoneId: string;
};

/**
 * `security/self` response. A password change revokes every token issued before it — including the
 * one that sent the request — so the backend returns a replacement in `token`; it is null otherwise.
 */
export type SelfUpdateResponseDTO = UserDetailsDTO & {
  token?: string | null;
};

export type UserOrganizationalUnitRoleDTO = {
  id: number;
  userId: number;
  roleId: number;
  organizationalUnitId: number;
  mainRole: boolean;
  canWorkAs: boolean;
  canBeAssigned: boolean;
  canAssign: boolean;
  canWorkUnassigned: boolean;
};

export type UserAuthDTO = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  jediId?: number;
  createdAt?: JSONDateTime;
  organizationalUnit: OrganizationalUnitInfoDTO;
  accessProfile: AccessProfileDTO;
  effectivePermissions: string[];
  organizationalUnitRoles: UserOrganizationalUnitRoleAuthDTO[];
  isActive: boolean;
  isDual: boolean;
  isSuperuser: boolean;
  hasCompletedProfile: boolean;
  changePassword: boolean;
  position: string;
};
export type OrganizationalUnitInfoDTO = {
  id: number;
  tenantId: number;
  parentId?: number;
  removed: boolean;
  code: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  state: string;
  path: string;
  numericCode: number;
  dependencyFigure: number;
  type: string;
  workflow: WorkflowDTO;
  workflowParams: JSONObject;
};
export type WorkflowDTO = {
  id: number;
  objectSchemaId: number;
  name: string;
  description: string;
  slug: string;
  redirect: string;
  viewForm: string;
  definition: JSONObject;
  paramSchema: JSONObject;
  roles: JSONArray;
  version: number;
  lastUpdated: JSONDateTime;
  workflowLayout: JSONObject;
};

export type AccessProfileDTO = {
  name: string;
  displayName: string;
  permissions: string[];
};
export type UserOrganizationalUnitRoleAuthDTO = {
  id: number;
  userId: number;
  roleId: number;
  organizationalUnitId: number;
  role: RoleDTO;
  organizationalUnit: OrganizationalUnitInfoDTO;
  mainRole: boolean;
  canWorkAs: boolean;
  canBeAssigned: boolean;
  canAssign: boolean;
  canWorkUnassigned: boolean;
};
export type RoleDTO = {
  name: string;
  displayName: string;
  extras: JSONObject;
  entities: JSONObject;
  removed: boolean;
};

export default AuthApi;
